import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import clientService from '../../../services/clientService';
import { ArrowLeft, Save, Briefcase, User, Calendar, DollarSign, FileText, Layers, Gauge } from 'lucide-react';

const ProjectForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    project_name: '',
    project_type: 'website',
    description: '',
    budget: '',
    paid_amount: 0,
    start_date: '',
    end_date: '',
    status: 'pending',
    progress: 0,
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadClients();
    if (isEditMode) loadProject();
  }, [id]);

  const loadClients = async () => {
    try {
      const res = await clientService.getClients({ per_page: 100 });
      setClients(res.data || []);
      // Prefill from query param when creating
      if (!isEditMode) {
        const qClientId = searchParams.get('client_id');
        if (qClientId) {
          setFormData(prev => ({ ...prev, client_id: qClientId }));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadProject = async () => {
    try {
      setLoading(true);
      const res = await clientService.getProject(id);
      setFormData({ ...res, budget: res.budget || '', paid_amount: res.paid_amount || 0 });
    } catch (e) {
      alert('Error loading project');
      navigate('/admin/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.client_id) e.client_id = 'Client is required';
    if (!formData.project_name.trim()) e.project_name = 'Project name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      const payload = { ...formData, budget: formData.budget ? Number(formData.budget) : null };
      if (isEditMode) {
        await clientService.updateProject(id, payload);
        alert('Project updated');
      } else {
        await clientService.createProject(payload);
        alert('Project created');
      }
      navigate('/admin/projects');
    } catch (e) {
      console.error(e);
      alert('Error saving project');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <button onClick={() => navigate('/admin/projects')} className="flex items-center gap-2 text-gray-600 hover:text-primary mb-4"><ArrowLeft className="w-5 h-5"/>Back to Projects</button>
        <h1 className="text-3xl font-bold text-primary mb-2">{isEditMode ? 'Edit Project' : 'Add New Project'}</h1>
        <p className="text-gray-600">Link to a client, set budget, dates and track progress</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5"/> Project Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Client *</label>
                  <select name="client_id" value={formData.client_id} onChange={handleChange} className={`w-full px-4 py-2 border-2 rounded-lg ${errors.client_id ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`}>
                    <option value="">Select Client</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                  {errors.client_id && <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Project Name *</label>
                  <input name="project_name" value={formData.project_name} onChange={handleChange} className={`w-full px-4 py-2 border-2 rounded-lg ${errors.project_name ? 'border-red-500' : 'border-gray-200 focus:border-primary'}`} placeholder="Hotel Website" />
                  {errors.project_name && <p className="text-red-500 text-sm mt-1">{errors.project_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Project Type</label>
                  <select name="project_type" value={formData.project_type} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg">
                    {['website','mobile_app','digital_marketing','seo','ecommerce','custom_software','amc','other'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Budget (฿)</label>
                  <input type="number" name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="4" className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" placeholder="Project scope, features..." />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><Calendar className="w-5 h-5"/> Timeline & Progress</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Start Date</label>
                  <input type="date" name="start_date" value={formData.start_date || ''} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">End Date</label>
                  <input type="date" name="end_date" value={formData.end_date || ''} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                  <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg">
                    {['pending','in_progress','completed','on_hold','cancelled'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"><Gauge className="w-4 h-4"/> Progress: {formData.progress}%</label>
                <input type="range" name="progress" min="0" max="100" value={formData.progress} onChange={(e)=>setFormData(prev=>({...prev, progress: parseInt(e.target.value,10)}))} className="w-full" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><FileText className="w-5 h-5"/> Notes</h2>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows="4" className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg" placeholder="Internal notes..." />
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Actions</h3>
              <button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? 'Saving...' : (<><Save className="w-5 h-5"/> {isEditMode ? 'Update Project' : 'Create Project'}</>)}
              </button>
              <button type="button" onClick={()=>navigate('/admin/projects')} className="w-full mt-3 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
